import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { merge } from 'lodash-es';
import {
  DashboardData,
  DashboardUserGroupData,
  UpdateDashboardFavoriteRequest,
  UpdateDashboardGroupRequest,
} from '@/dashboard/shared/types/types';
import {
  postV6DashboardsDashboardGroups,
  postV6DashboardsDashboard,
  postV6DashboardsFavorite,
  postV6DashboardsGroup,
  patchV6DashboardsCustomReport,
} from '@/api-client/dashboard/queries/integrated-dashboard-controller/integrated-dashboard-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import {
  convertToDashboardData,
  convertToDeleteDashboardsDto,
  convertToUpdateDashboardFavoriteRequestDto,
  convertToUpdateDashboardGroupRequestDto,
} from '@/dashboard/features/dashboardCore/services/dashboard';
import { PLATFORM_MENU } from '@/common/stores/platform-dashboard';
import { getV8Dashboards } from '@/api-client/metav8Over/queries/integrated-dashboard-v8-controller/integrated-dashboard-v8-controller';
import type {
  IntegrateDashboardComponentExtends,
  IntegrateDashboardPatchResponse,
  DashboardGroupMoveRequest,
  GetV7DashboardsGroupParams,
  IntegrateDashboardGroupV7ResponseItem,
} from '@/api-client/dashboard/model';
import { PromiseAxiosResponse } from '@/worker/commands/config/apiInstance';
import { getV7DashboardsGroup } from '@/api-client/dashboard/queries/integrated-dashboard-group-controller/integrated-dashboard-group-controller';
import { DashboardCardDataType } from '@/home/widgets/dashboardCard/types';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import { useUserEnvStore } from '@/common/stores/user-env';
import { convertMenuPaths } from '@/dashboardList/utils/menu.utils';
import { i18n } from '@/common/locale';
import { useViewModeStore } from '@/common/stores/view-mode';
import type { ValueOf } from '@/types/common.types';
import { DEFAULT_DASHBOARD_GROUP_ID } from '../features/widgets/common/constants/define';

const envKey = USER_ENV_KEYS.DISPLAYOPTION_dashboard_main_view;

const FEATURED_DASHBOARD_GROUP_LANGUAGE_KEY: Record<
  ValueOf<typeof DEFAULT_DASHBOARD_GROUP_ID>,
  string
> = {
  all: 'WORD.ALL',
  featuredDashboard: 'WORD.PRESET_DASHBOARD',
  featuredReport: 'WORD.FEATURED_REPORT',
  favorite: 'WORD.FAVORITE',
  shared: 'WORD.SHARED',
  customReport: 'WORD.REPORT',
} as const;

interface MainViewEnvType {
  selectedTab: string;
  showFavoriteOnly: boolean;
  showBoardType: string;
  showDisplayType: string;
  isExpandRecentVisit: boolean;
  isExpandRecentShare: boolean;
}

const initMainViewEnv: MainViewEnvType = {
  selectedTab: 'all',
  showFavoriteOnly: false,
  showBoardType: 'custom',
  showDisplayType: 'list',
  isExpandRecentVisit: true,
  isExpandRecentShare: true,
};

export const useDashboardListStore = defineStore('dashboardDashboardList', () => {
  const { getEnvValue, setEnvValue } = useUserEnvStore();
  const { isMaxgaugeMode } = storeToRefs(useViewModeStore());

  const mainViewEnv = ref<MainViewEnvType>(initMainViewEnv);
  const setEnv = () => {
    setEnvValue([{ key: envKey, value: JSON.stringify(mainViewEnv.value) }]);
  };
  const initEnv = async () => {
    const savedEnv = getEnvValue(envKey);
    if (savedEnv) {
      mainViewEnv.value = merge(mainViewEnv.value, savedEnv);
    } else {
      mainViewEnv.value = initMainViewEnv;
    }
  };
  // mainViewEnv 변경 시 저장
  watch(() => mainViewEnv, setEnv, { deep: true });

  // 데이터 갱신 트리거
  const refreshCounter = ref<number>(0);
  const refreshTrigger = () => {
    refreshCounter.value += 1;
  };

  const getDashboardTabTypeParam = (v?: string) => {
    switch (v) {
      case 'all':
        return undefined;
      default:
        return v;
    }
  };

  const dashboardUserGroupList = ref<IntegrateDashboardGroupV7ResponseItem[]>([]);
  const dashboardUserGroupListByTab = ref<IntegrateDashboardGroupV7ResponseItem[]>([]);
  const dashboardAllGroupNames = ref<string[]>([]);
  const dashboardList = ref<DashboardData[]>([]); // 이미지 값 없는 데이터. 목록뷰에서 사용.
  const dashboardListByTab = ref<DashboardData[]>([]); // 이미지 값 없는 데이터. 목록뷰에서 사용.

  const fetchDashboardUserGroupList = async () => {
    try {
      const { data } = await getV7DashboardsGroup(
        { withTotal: false },
        { params: { frameName: FRAME_NAMES.DASHBOARD.DASHBOARD_USER_GROUP } },
      );
      dashboardUserGroupList.value = data?.data ?? [];
    } catch (e) {
      console.log(e);
    }
  };

  const fetchDashboardUserGroupListByTab = async () => {
    const dashboardTapType = getDashboardTabTypeParam(mainViewEnv.value.selectedTab);

    try {
      const { data } = await getV7DashboardsGroup(
        {
          dashboardTapType: dashboardTapType as GetV7DashboardsGroupParams['dashboardTapType'],
        },
        { params: { frameName: FRAME_NAMES.DASHBOARD.DASHBOARD_USER_GROUP } },
      );
      dashboardUserGroupListByTab.value = data?.data ?? [];
    } catch (e) {
      console.log(e);
    }
  };

  const isExemOnePlatform = (platform: string): boolean =>
    Object.values<string>(PLATFORM_MENU).includes(platform);

  const filterMenuPaths = (menuPaths: string[]): string[] => {
    let filteredValues: string[];
    if (isMaxgaugeMode.value) {
      filteredValues = menuPaths.filter((item) => item !== 'database');
    } else {
      const exemOnePlatform = menuPaths.filter((item) => isExemOnePlatform(item));
      const isExistMaxGaugePlatform = menuPaths.some((item) => !isExemOnePlatform(item));
      if (isExistMaxGaugePlatform && !menuPaths.includes('database')) {
        filteredValues = [...exemOnePlatform, 'database'];
      } else {
        filteredValues = exemOnePlatform;
      }
    }
    return filteredValues;
  };

  const fetchDashboardList = async (isImage = false) => {
    try {
      const { data } = await getV8Dashboards(
        { includeImage: isImage, withFeatured: true },
        { params: { frameName: FRAME_NAMES.DASHBOARD.DASHBOARD_LIST } },
      );
      const dashboardInfos = data.data ?? [];
      const dashboardListData = dashboardInfos.map(convertToDashboardData).map((info) => ({
        ...info,
        menuPaths: filterMenuPaths(convertMenuPaths(info.menuPaths ?? [])),
        analysisMenuPaths: filterMenuPaths(convertMenuPaths(info.analysisMenuPaths ?? [])),
      }));
      dashboardList.value = dashboardListData;
    } catch (e) {
      console.log(e);
    }
  };

  const fetchDashboardListByTab = async () => {
    const dashboardTapType = getDashboardTabTypeParam(mainViewEnv.value.selectedTab);
    try {
      const { data } = await getV8Dashboards(
        {
          dashboardTapType: dashboardTapType as GetV7DashboardsGroupParams['dashboardTapType'],
        },
        { params: { frameName: FRAME_NAMES.DASHBOARD.DASHBOARD_LIST } },
      );
      const dashboardInfos = data.data ?? [];
      const dashboardListData = dashboardInfos.map(convertToDashboardData).map((info) => ({
        ...info,
        menuPaths: filterMenuPaths(convertMenuPaths(info.menuPaths ?? [])),
        analysisMenuPaths: filterMenuPaths(convertMenuPaths(info.analysisMenuPaths ?? [])),
      }));
      dashboardList.value = dashboardListData; // note: 개선 전 대시보드 화면에 영향없도록 하기 위함
      dashboardListByTab.value = dashboardListData;
    } catch (e) {
      console.log(e);
    }
  };

  const isUpdatingDashboardUserGroup = ref<boolean>(false);
  const updateDashboardUserGroup = async (
    { dashboardGroupId, dashboardGroupName }: DashboardUserGroupData,
    deleteWithDashboard = false,
    deleted = false,
    frameName = '',
  ) => {
    try {
      isUpdatingDashboardUserGroup.value = true;
      await postV6DashboardsGroup(
        {
          dashboardGroupId,
          dashboardGroupName,
          deleteWithDashboard,
          deleted,
        },
        {},
        { frameName },
      );
    } catch (e) {
      console.log(e);
      throw e;
    } finally {
      isUpdatingDashboardUserGroup.value = false;
    }
  };

  const addDashboardList = async (
    dashboards: IntegrateDashboardComponentExtends[],
  ): PromiseAxiosResponse<IntegrateDashboardPatchResponse> => {
    return postV6DashboardsDashboard({
      dashboards,
    });
  };

  const updateDashboardList = async (
    dashboards: IntegrateDashboardComponentExtends[],
    frameName?: string,
  ) => {
    if (!dashboards.length) {
      return;
    }

    await postV6DashboardsDashboard({ dashboards }, { frameName });

    await fetchDashboardList();
  };

  const deleteDashboardList = async (dashboardIds: number[]) => {
    const deletedDashboards: DashboardData[] = dashboardList.value.reduce(
      (acc: DashboardData[], dashboard) => {
        if (dashboard.dashboardId && dashboardIds.includes(dashboard.dashboardId)) {
          acc.push({
            ...dashboard,
            dashboardGroupId: -1,
          });
        }
        return acc;
      },
      [],
    );

    await updateDashboardList(
      deletedDashboards.map(convertToDeleteDashboardsDto),
      FRAME_NAMES.DASHBOARD.DASHBOARD_LIST_UPDATE,
    );
    refreshTrigger();
  };

  const updateDashboardListGroup = async (
    dashboards: DashboardGroupMoveRequest[],
    frameName?: string,
  ) => {
    await postV6DashboardsDashboardGroups(
      {
        dashboards,
      },
      { frameName },
    );

    await fetchDashboardList();
    refreshTrigger();
  };

  const moveGroupForDashboardList = async (dashboardIds: number[], newGroupId: string | null) => {
    const movedDashboards: UpdateDashboardGroupRequest[] = [];
    dashboardList.value.forEach((dashboard) => {
      if (dashboard.dashboardId && dashboardIds.includes(dashboard.dashboardId)) {
        movedDashboards.push({
          dashboardId: dashboard.dashboardId,
          dashboardGroupId: newGroupId ? +newGroupId : undefined,
        });
      }
    });

    await updateDashboardListGroup(
      movedDashboards.map(convertToUpdateDashboardGroupRequestDto),
      FRAME_NAMES.DASHBOARD.DASHBOARD_USER_GROUP_MOVE,
    );
  };

  const updateFavoriteDashboardList = async (dashboardIds: number[]) => {
    if (!dashboardIds.length) {
      return;
    }

    const dashboards: UpdateDashboardFavoriteRequest[] = [];
    dashboardList.value.forEach((dashboard) => {
      if (dashboard.dashboardId && dashboardIds.includes(dashboard.dashboardId)) {
        dashboards.push({
          dashboardId: dashboard.dashboardId,
          favoriteId: dashboard.favoriteId,
          deleted: !!dashboard.favoriteId,
        });
      }
    });

    try {
      await postV6DashboardsFavorite(
        {
          dashboards: dashboards.map(convertToUpdateDashboardFavoriteRequestDto),
        },
        {},
        { frameName: FRAME_NAMES.DASHBOARD.FAVORITE_DASHBOARD_UPDATE },
      );
      await fetchDashboardList();
      refreshTrigger();
    } catch (e) {
      console.log(e);
    }
  };

  const setReportDashboard = async (dashboardId: number, customReport: boolean) => {
    try {
      await patchV6DashboardsCustomReport(
        {
          dashboardId,
          customReport,
        },
        { params: { frameName: FRAME_NAMES.DASHBOARD.REPORT_DASHBOARD_SET } },
      );

      await fetchDashboardList();
      refreshTrigger();
    } catch (e) {
      console.log(e);
    }
  };

  const openAddDashboardImmediately = ref<boolean>(false);
  const setOpenAddDashboardImmediately = (val: boolean) => {
    openAddDashboardImmediately.value = val;
  };

  const filterFavoriteOnly = (dashboard: DashboardCardDataType) => {
    if (!mainViewEnv.value.showFavoriteOnly) return true;
    return !!dashboard.favoriteId === mainViewEnv.value.showFavoriteOnly;
  };

  const dashboardFeaturedGroupNames = computed<string[]>(() => {
    return Object.values(FEATURED_DASHBOARD_GROUP_LANGUAGE_KEY)
      .map((key) => {
        return i18n.global.availableLocales.map((locale) => i18n.global.t(key, {}, { locale }));
      })
      .flat();
  });

  const favoriteDashboardCountByGroup = computed<Map<string, number>>(() => {
    const map = new Map<string, number>();

    let allCount = 0;

    dashboardListByTab.value.forEach((dashboard) => {
      if (dashboard.favoriteId != null) {
        const key = String(dashboard.dashboardGroupId);
        map.set(key, (map.get(key) || 0) + 1);
        allCount++;
      }
    });

    map.set('all', allCount);

    return map;
  });

  const getFavoriteDashboardCountByGroup = (groupId: string): number => {
    return favoriteDashboardCountByGroup.value.get(groupId) ?? 0;
  };

  const getDashboardCountByGroup = (groupId: string, typeCount: number): number => {
    if (mainViewEnv.value.showFavoriteOnly) {
      return getFavoriteDashboardCountByGroup(groupId);
    }
    return typeCount;
  };

  watch(dashboardUserGroupList, (userGroups) => {
    dashboardAllGroupNames.value = [
      ...dashboardFeaturedGroupNames.value,
      ...userGroups.map((group) => group.dashboardGroupName).filter((name) => name != null),
    ];
  });

  return {
    mainViewEnv,
    initEnv,
    refreshCounter,
    refreshTrigger,
    getFavoriteDashboardCountByGroup,
    getDashboardCountByGroup,

    dashboardUserGroupList,
    dashboardAllGroupNames,
    fetchDashboardUserGroupList,
    dashboardList,
    fetchDashboardList,
    dashboardUserGroupListByTab,
    fetchDashboardUserGroupListByTab,
    dashboardListByTab,
    fetchDashboardListByTab,
    isUpdatingDashboardUserGroup,
    updateDashboardUserGroup,
    addDashboardList,
    updateDashboardList,
    deleteDashboardList,
    moveGroupForDashboardList,
    updateFavoriteDashboardList,
    setReportDashboard,
    openAddDashboardImmediately,
    setOpenAddDashboardImmediately,

    filterFavoriteOnly,
  };
});
